/**
 * Anthropic Admin Usage API client.
 *
 * Queries /v1/organizations/usage_report/messages for ground-truth
 * billing data. Requires an admin API key (sk-ant-admin...).
 */

// ── Types ────────────────────────────────────────────────────

export interface UsageBucket {
  startAt: string;
  endAt: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  cacheCreationInputTokens: number;
}

export interface AdminUsageReport {
  buckets: UsageBucket[];
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens: number;
  totalCacheCreationTokens: number;
}

export interface AdminUsageOptions {
  adminKey: string;
  startingAt: string; // ISO 8601 date
  endingAt: string; // ISO 8601 date
  bucketWidth?: "1m" | "1h" | "1d";
  groupBy?: string[];
  baseUrl?: string;
}

// ── API response types (matches Anthropic Admin API) ─────────

interface ApiCacheCreation {
  ephemeral_5m_input_tokens?: number;
  ephemeral_1h_input_tokens?: number;
}

interface ApiResult {
  model?: string;
  uncached_input_tokens?: number;
  output_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation?: ApiCacheCreation;
  [key: string]: unknown;
}

interface ApiBucket {
  starting_at?: string;
  ending_at?: string;
  results?: ApiResult[];
}

interface ApiResponse {
  data?: ApiBucket[];
  has_more?: boolean;
  next_page?: string | null;
}

// ── Client ───────────────────────────────────────────────────

/**
 * Fetch usage report from the Anthropic Admin API.
 * Returns null on any error (invalid key, rate limit, network failure).
 *
 * API docs: https://docs.anthropic.com/en/api/admin-api/usage-cost/get-messages-usage-report
 * Response uses nested data[].results[] structure with model-level breakdowns.
 */
export async function fetchAdminUsage(
  options: AdminUsageOptions,
): Promise<AdminUsageReport | null> {
  const {
    adminKey,
    startingAt,
    endingAt,
    bucketWidth = "1d",
    groupBy = ["model"],
    baseUrl = "https://api.anthropic.com",
  } = options;

  const params = new URLSearchParams({
    starting_at: startingAt,
    ending_at: endingAt,
    bucket_width: bucketWidth,
  });
  // API expects group_by[] array syntax
  for (const g of groupBy) {
    params.append("group_by[]", g);
  }

  const allBuckets: UsageBucket[] = [];
  let totalInputTokens = 0;
  let totalOutputTokens = 0;
  let totalCacheReadTokens = 0;
  let totalCacheCreationTokens = 0;
  let nextPage: string | null = null;

  try {
    // Paginate through all pages
    do {
      const pageUrl = nextPage
        ? `${baseUrl}${nextPage}`
        : `${baseUrl}/v1/organizations/usage_report/messages?${params}`;

      const response = await fetch(pageUrl, {
        headers: {
          "x-api-key": adminKey,
          "anthropic-version": "2023-06-01",
        },
      });

      if (!response.ok) {
        return null;
      }

      const body = (await response.json()) as ApiResponse;
      if (!body.data || !Array.isArray(body.data)) {
        return null;
      }

      for (const timeBucket of body.data) {
        const bucketStart = timeBucket.starting_at ?? "";
        const bucketEnd = timeBucket.ending_at ?? "";

        for (const result of timeBucket.results ?? []) {
          // Cache creation tokens are split across TTL tiers
          const cacheCreation =
            (result.cache_creation?.ephemeral_5m_input_tokens ?? 0) +
            (result.cache_creation?.ephemeral_1h_input_tokens ?? 0);

          const bucket: UsageBucket = {
            startAt: bucketStart,
            endAt: bucketEnd,
            model: result.model ?? "unknown",
            inputTokens: result.uncached_input_tokens ?? 0,
            outputTokens: result.output_tokens ?? 0,
            cacheReadInputTokens: result.cache_read_input_tokens ?? 0,
            cacheCreationInputTokens: cacheCreation,
          };

          totalInputTokens += bucket.inputTokens;
          totalOutputTokens += bucket.outputTokens;
          totalCacheReadTokens += bucket.cacheReadInputTokens;
          totalCacheCreationTokens += bucket.cacheCreationInputTokens;

          allBuckets.push(bucket);
        }
      }

      nextPage = body.has_more ? (body.next_page ?? null) : null;
    } while (nextPage);

    return {
      buckets: allBuckets,
      totalInputTokens,
      totalOutputTokens,
      totalCacheReadTokens,
      totalCacheCreationTokens,
    };
  } catch {
    return null;
  }
}

/**
 * Resolve the admin API key from config or environment.
 * Priority: explicit argument > ANTHROPIC_ADMIN_KEY env var > config file.
 */
export function resolveAdminKey(
  configKey?: string,
): string | null {
  return (
    process.env.ANTHROPIC_ADMIN_KEY ??
    configKey ??
    null
  );
}
